import { PastEvent, DJProfile } from '../../../lib/supabase';

interface PastEventsProps {
  events: PastEvent[];
  profile?: DJProfile | null;
}

export default function PastEvents({ events, profile }: PastEventsProps) {
  const subtitle = profile?.section_past_events_subtitle || 'Eventos Pasados';
  const title = profile?.section_past_events_title || 'Experiencias\nInolvidables';
  const description = profile?.section_past_events_description || 'He tenido el honor de participar en eventos increíbles, llevando la mejor música a miles de personas.';

  return (
    <section id="past-events" className="py-24 lg:py-32 bg-[#1a1a1a] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, cyan 2px, cyan 4px)' }}></div>
      </div>
      
      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <div className="max-w-3xl mb-16 pl-2 lg:pl-4">
          <span className="text-cyan-400 text-xs uppercase tracking-[0.3em] font-light">{subtitle}</span>
          <h2 className="text-4xl lg:text-5xl font-black text-white mt-4 leading-tight">
            {title.split('\n').map((line, i) => (
              <span key={i}>{line}{i < title.split('\n').length - 1 && <br />}</span>
            ))}
          </h2>
          <p className="text-gray-400 text-base mt-6 leading-relaxed">
            {description}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map((event, index) => (
            <div 
              key={event.id}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:scale-[1.02] ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              }`}
            >
              <div className="relative aspect-[4/3] w-full h-full">
                <img 
                  src={event.image_url}
                  alt={event.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
                
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent opacity-70 group-hover:opacity-50 transition-opacity duration-300"></div>
                
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-cyan-400 to-fuchsia-400"></div>
                
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-white font-bold text-xl lg:text-2xl mb-2 leading-tight">
                      {event.title}
                    </h3>
                    <p className="text-gray-300 text-sm mb-3 line-clamp-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {event.description}
                    </p>
                    <div className="flex items-center gap-4 text-gray-400 text-sm">
                      <span className="flex items-center gap-2">
                        <i className="ri-calendar-line"></i>
                        {new Date(event.event_date).toLocaleDateString('es-ES', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                      <span className="flex items-center gap-2">
                        <i className="ri-map-pin-line"></i>
                        {event.location}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
